import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, IconButton, CircularProgress, Alert, Snackbar, Divider,
  MenuItem, Tooltip, Collapse, Switch, FormControlLabel,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Add as AddIcon, Payments as PayIcon, Delete as DeleteIcon, Edit as EditIcon,
  ExpandMore as ExpandIcon, ExpandLess as CollapseIcon, Groups as PersonIcon,
} from '@mui/icons-material';
import apiService from '../../services/api';

/**
 * La cuenta de quien trae clientes: RD$X por cada cliente que se consigue y se instala.
 *
 * Se muestra como cuenta corriente —acumulado, pagado, saldo— porque es como se lleva de
 * cabeza, y el detalle de instalaciones está a un clic para poder responder "¿cuáles?" sin
 * salir de aquí. Los clientes de dealers no cuentan: esos no los consigue nadie.
 */

const money = (n: number) =>
  `RD$${(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const nombreMes = (m: string) => {
  const [a, mm] = m.split('-').map(Number);
  return `${MESES[mm - 1]} ${a}`;
};
const fecha = (d?: string | null) => (d ? new Date(d).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const hoyISO = () => new Date().toISOString().slice(0, 10);

const METODOS = [
  { v: 'efectivo', l: 'Efectivo' },
  { v: 'transferencia', l: 'Transferencia' },
  { v: 'otro', l: 'Otro' },
];

interface Pago {
  _id: string; date: string; amount: number; method: string; note?: string; expenseId?: string | null;
}
interface Partner {
  _id: string; name: string; ratePerInstallation: number; startDate: string;
  excludeDealers: boolean; active: boolean; notes?: string;
  instalacionesContadas: number; devengado: number; totalPagado: number; saldo: number;
  desdeUltimoPago: { fecha: string | null; instalaciones: number; monto: number };
  porMes: { mes: string; instalaciones: number; monto: number }[];
  pagos: Pago[];
}

const ComisionReferidosPage: React.FC = () => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const [abierto, setAbierto] = useState<string | null>(null);
  const [detalle, setDetalle] = useState<Record<string, any>>({});
  const [verTodas, setVerTodas] = useState(false);

  const [pagoDe, setPagoDe] = useState<Partner | null>(null);
  const [pagoMonto, setPagoMonto] = useState('');
  const [pagoMetodo, setPagoMetodo] = useState('efectivo');
  const [pagoNota, setPagoNota] = useState('');
  const [pagoFecha, setPagoFecha] = useState(hoyISO());
  const [guardando, setGuardando] = useState(false);

  const [nuevoAbierto, setNuevoAbierto] = useState(false);
  const [nuevo, setNuevo] = useState({ name: '', ratePerInstallation: '200', startDate: hoyISO() });

  const [editando, setEditando] = useState<Partner | null>(null);
  const [edit, setEdit] = useState({ ratePerInstallation: '', startDate: '', excludeDealers: true, active: true });

  const cargar = useCallback(async () => {
    try {
      setLoading(true);
      const res: any = await apiService.getReferralPartners();
      setPartners(res?.partners || []);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo cargar la cuenta de comisiones');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const cargarDetalle = useCallback(async (id: string, todas: boolean) => {
    try {
      const res: any = await apiService.getReferralInstallations(id, todas);
      setDetalle((p) => ({ ...p, [id]: res }));
    } catch {
      setAviso('No se pudo cargar el detalle de instalaciones');
    }
  }, []);

  const alternar = (p: Partner) => {
    const nuevoId = abierto === p._id ? null : p._id;
    setAbierto(nuevoId);
    if (nuevoId && !detalle[nuevoId]) cargarDetalle(nuevoId, verTodas);
  };

  const cambiarAlcance = (id: string, todas: boolean) => {
    setVerTodas(todas);
    cargarDetalle(id, todas);
  };

  const abrirPago = (p: Partner) => {
    setPagoDe(p);
    // Se propone el saldo completo: el caso normal es liquidar y dejar la cuenta en cero.
    setPagoMonto(String(p.saldo > 0 ? p.saldo : ''));
    setPagoMetodo('efectivo');
    setPagoNota('');
    setPagoFecha(hoyISO());
  };

  const registrarPago = async () => {
    if (!pagoDe) return;
    const monto = Number(pagoMonto);
    if (!monto || monto <= 0) { setAviso('Pon un monto mayor que cero'); return; }
    try {
      setGuardando(true);
      await apiService.payReferralPartner(pagoDe._id, {
        amount: monto, method: pagoMetodo, note: pagoNota || undefined, date: pagoFecha,
      });
      setPagoDe(null);
      setDetalle({});
      await cargar();
      setAviso(`Pago de ${money(monto)} registrado. También quedó anotado como gasto.`);
    } catch (e: any) {
      setAviso(e?.response?.data?.message || 'No se pudo registrar el pago');
    } finally {
      setGuardando(false);
    }
  };

  const borrarPago = async (p: Partner, pago: Pago) => {
    if (!window.confirm(`¿Eliminar el pago de ${money(pago.amount)} del ${fecha(pago.date)}?\n\nEl saldo volverá a subir y el gasto se borra junto.`)) return;
    try {
      await apiService.deleteReferralPayout(p._id, pago._id);
      setDetalle({});
      await cargar();
      setAviso('Pago eliminado');
    } catch (e: any) {
      setAviso(e?.response?.data?.message || 'No se pudo eliminar');
    }
  };

  const crear = async () => {
    if (!nuevo.name.trim()) { setAviso('Falta el nombre'); return; }
    try {
      setGuardando(true);
      await apiService.createReferralPartner({
        name: nuevo.name.trim(),
        ratePerInstallation: Number(nuevo.ratePerInstallation) || 0,
        startDate: nuevo.startDate,
      });
      setNuevoAbierto(false);
      setNuevo({ name: '', ratePerInstallation: '200', startDate: hoyISO() });
      await cargar();
      setAviso('Colaborador agregado');
    } catch (e: any) {
      setAviso(e?.response?.data?.message || 'No se pudo crear');
    } finally {
      setGuardando(false);
    }
  };

  const borrarPersona = async (p: Partner) => {
    if (!window.confirm(`¿Quitar a ${p.name} de la lista?`)) return;
    try {
      await apiService.deleteReferralPartner(p._id);
      setEditando(null);
      await cargar();
      setAviso('Eliminado');
    } catch (e: any) {
      setAviso(e?.response?.data?.message || 'No se pudo eliminar');
    }
  };

  const abrirEdicion = (p: Partner) => {
    setEditando(p);
    setEdit({
      ratePerInstallation: String(p.ratePerInstallation),
      startDate: p.startDate.slice(0, 10),
      excludeDealers: p.excludeDealers,
      active: p.active,
    });
  };

  const guardarEdicion = async () => {
    if (!editando) return;
    try {
      setGuardando(true);
      await apiService.updateReferralPartner(editando._id, {
        ratePerInstallation: Number(edit.ratePerInstallation) || 0,
        startDate: edit.startDate,
        excludeDealers: edit.excludeDealers,
        active: edit.active,
      });
      setEditando(null);
      setDetalle({});
      await cargar();
      setAviso('Actualizado. El acumulado se recalculó completo.');
    } catch (e: any) {
      setAviso(e?.response?.data?.message || 'No se pudo actualizar');
    } finally {
      setGuardando(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Comisión por referidos</Typography>
          <Typography variant="body2" color="text.secondary">
            Quien trae clientes cobra por cada uno que se instala. Los clientes de dealers no cuentan.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setNuevoAbierto(true)}>
          Agregar persona
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {partners.length === 0 && (
        <Card variant="outlined"><CardContent>
          <Typography color="text.secondary">
            Nadie registrado todavía. Agrega a quien te trae clientes y la cuenta se lleva sola.
          </Typography>
        </CardContent></Card>
      )}

      {partners.map((p) => {
        const d = detalle[p._id];
        return (
          <Card key={p._id} variant="outlined" sx={{ mb: 2, opacity: p.active ? 1 : 0.6 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon color="action" />
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{p.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {money(p.ratePerInstallation)} por instalación · desde {fecha(p.startDate)}
                      </Typography>
                    </Box>
                    <Tooltip title="Cambiar tarifa o fecha de inicio">
                      <IconButton size="small" onClick={() => abrirEdicion(p)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 5 }}>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Acumulado</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {money(p.devengado)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {p.instalacionesContadas} instalaciones
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Pagado</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{money(p.totalPagado)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Debe cobrar</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: p.saldo > 0 ? 'warning.dark' : 'success.main' }}>
                        {money(p.saldo)}
                      </Typography>
                      {p.saldo === 0 && <Typography variant="caption" color="success.main">al día</Typography>}
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: { md: 'flex-end' }, flexWrap: 'wrap' }}>
                    <Button variant="contained" startIcon={<PayIcon />} onClick={() => abrirPago(p)}>
                      Registrar pago
                    </Button>
                    <Button
                      size="small"
                      endIcon={abierto === p._id ? <CollapseIcon /> : <ExpandIcon />}
                      onClick={() => alternar(p)}
                    >
                      Detalle
                    </Button>
                  </Box>
                </Grid>
              </Grid>

              {p.desdeUltimoPago.fecha && p.desdeUltimoPago.instalaciones > 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <strong>{p.desdeUltimoPago.instalaciones} instalaciones</strong> nuevas desde el último pago
                  ({fecha(p.desdeUltimoPago.fecha)}) = {money(p.desdeUltimoPago.monto)}.
                  {p.saldo !== p.desdeUltimoPago.monto && ' El saldo es mayor porque ese pago fue un abono parcial.'}
                </Alert>
              )}

              <Collapse in={abierto === p._id} unmountOnExit>
                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Por mes</Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Mes</TableCell>
                        <TableCell align="right">Instalaciones</TableCell>
                        <TableCell align="right">Le toca</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {p.porMes.map((m) => (
                        <TableRow key={m.mes}>
                          <TableCell sx={{ textTransform: 'capitalize' }}>{nombreMes(m.mes)}</TableCell>
                          <TableCell align="right">{m.instalaciones}</TableCell>
                          <TableCell align="right">{money(m.monto)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Pagos</Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableBody>
                      {p.pagos.length === 0 && (
                        <TableRow><TableCell colSpan={4}>
                          <Typography variant="body2" color="text.secondary">Sin pagos registrados</Typography>
                        </TableCell></TableRow>
                      )}
                      {p.pagos.map((pago) => (
                        <TableRow key={pago._id}>
                          <TableCell>{fecha(pago.date)}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{money(pago.amount)}</TableCell>
                          <TableCell>
                            <Chip size="small" label={METODOS.find((m) => m.v === pago.method)?.l || pago.method} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">{pago.note || ''}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Eliminar (si fue un error de tecleo)">
                              <IconButton size="small" onClick={() => borrarPago(p, pago)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Instalaciones que cuentan {d ? `(${d.total})` : ''}
                  </Typography>
                  <FormControlLabel
                    control={<Switch size="small" checked={verTodas} onChange={(e) => cambiarAlcance(p._id, e.target.checked)} />}
                    label={<Typography variant="caption">Ver todo el histórico</Typography>}
                  />
                </Box>
                {!d && <CircularProgress size={20} />}
                {d && (
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>Fecha</TableCell>
                          <TableCell>Cliente</TableCell>
                          <TableCell>IMEI</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {d.instalaciones.length === 0 && (
                          <TableRow><TableCell colSpan={4}>
                            <Typography variant="body2" color="text.secondary">
                              Nada nuevo desde el último pago.
                            </Typography>
                          </TableCell></TableRow>
                        )}
                        {d.instalaciones.map((i: any, idx: number) => (
                          <TableRow key={i._id}>
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell>{fecha(i.installationDate)}</TableCell>
                            <TableCell>{i.clientName || '—'}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{i.imei || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Collapse>
            </CardContent>
          </Card>
        );
      })}

      {/* Registrar pago */}
      <Dialog open={!!pagoDe} onClose={() => setPagoDe(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Pagarle a {pagoDe?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Debe cobrar <strong>{money(pagoDe?.saldo || 0)}</strong>. Si le das menos, el resto queda
            como saldo pendiente.
          </Typography>
          <TextField
            label="Monto" type="number" fullWidth sx={{ mb: 2 }}
            value={pagoMonto} onChange={(e) => setPagoMonto(e.target.value)}
            InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>RD$</Typography> }}
          />
          <TextField
            select label="Cómo se le pagó" fullWidth sx={{ mb: 2 }}
            value={pagoMetodo} onChange={(e) => setPagoMetodo(e.target.value)}
          >
            {METODOS.map((m) => <MenuItem key={m.v} value={m.v}>{m.l}</MenuItem>)}
          </TextField>
          <TextField
            label="Fecha" type="date" fullWidth sx={{ mb: 2 }}
            value={pagoFecha} onChange={(e) => setPagoFecha(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Nota (opcional)" fullWidth multiline rows={2}
            value={pagoNota} onChange={(e) => setPagoNota(e.target.value)}
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            El pago se anota también como gasto en la categoría «Comisiones por referidos».
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPagoDe(null)}>Cancelar</Button>
          <Button variant="contained" onClick={registrarPago} disabled={guardando}>
            {guardando ? 'Guardando…' : 'Registrar pago'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Nueva persona */}
      <Dialog open={nuevoAbierto} onClose={() => setNuevoAbierto(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Agregar quien trae clientes</DialogTitle>
        <DialogContent>
          <TextField
            label="Nombre" fullWidth sx={{ mb: 2, mt: 1 }}
            value={nuevo.name} onChange={(e) => setNuevo({ ...nuevo, name: e.target.value })}
          />
          <TextField
            label="Por instalación" type="number" fullWidth sx={{ mb: 2 }}
            value={nuevo.ratePerInstallation}
            onChange={(e) => setNuevo({ ...nuevo, ratePerInstallation: e.target.value })}
            InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>RD$</Typography> }}
          />
          <TextField
            label="Cuenta desde" type="date" fullWidth
            value={nuevo.startDate} onChange={(e) => setNuevo({ ...nuevo, startDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            helperText="Las instalaciones anteriores a esta fecha no se le deben"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNuevoAbierto(false)}>Cancelar</Button>
          <Button variant="contained" onClick={crear} disabled={guardando}>Agregar</Button>
        </DialogActions>
      </Dialog>

      {/* Editar tarifa / fecha */}
      <Dialog open={!!editando} onClose={() => setEditando(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{editando?.name}</DialogTitle>
        <DialogContent>
          <TextField
            label="Por instalación" type="number" fullWidth sx={{ mb: 2, mt: 1 }}
            value={edit.ratePerInstallation}
            onChange={(e) => setEdit({ ...edit, ratePerInstallation: e.target.value })}
            InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>RD$</Typography> }}
          />
          <TextField
            label="Cuenta desde" type="date" fullWidth
            value={edit.startDate} onChange={(e) => setEdit({ ...edit, startDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={edit.excludeDealers}
                onChange={(e) => setEdit({ ...edit, excludeDealers: e.target.checked })}
              />
            }
            label={<Typography variant="body2">No contar los clientes de dealers</Typography>}
          />
          <FormControlLabel
            control={
              <Switch
                checked={edit.active}
                onChange={(e) => setEdit({ ...edit, active: e.target.checked })}
              />
            }
            label={<Typography variant="body2">Cuenta activa</Typography>}
          />
          <Alert severity="warning" sx={{ mt: 2 }}>
            Cambiar la tarifa o la fecha recalcula todo el acumulado, no solo lo nuevo.
          </Alert>
        </DialogContent>
        <DialogActions>
          {editando && editando.pagos.length === 0 && (
            <Button color="error" onClick={() => borrarPersona(editando)} sx={{ mr: 'auto' }}>
              Eliminar
            </Button>
          )}
          <Button onClick={() => setEditando(null)}>Cancelar</Button>
          <Button variant="contained" onClick={guardarEdicion} disabled={guardando}>Guardar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!aviso} autoHideDuration={5000} onClose={() => setAviso(null)}
        message={aviso || ''} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default ComisionReferidosPage;
